import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo.ts';
import { useAuthStore } from '../state/authStore.ts';

/**
 * European Accessibility Act penalties reference. A factual, sourced overview of how
 * member-state penalties work under national transpositions of Directive 2019/882.
 *
 * This page is informational only — it presents published statutory caps, not an estimate
 * of any individual business's exposure, and explicitly defers to qualified legal counsel.
 * It is intentionally free of fear-based framing or "what this could cost you" mechanics.
 */

const PENALTIES: ReadonlyArray<{ country: string; cap: string; statute: string }> = [
  { country: 'Germany',     cap: 'Up to €100,000 per offence',     statute: '§22 Barrierefreiheitsstärkungsgesetz (BFSG)' },
  { country: 'France',      cap: 'Up to €25,000 per offence',      statute: 'Décret 2023-931 (loi 2023-661)' },
  { country: 'Italy',       cap: 'Up to €40,000 per offence',      statute: 'D.Lgs. 82/2022, art. 9' },
  { country: 'Spain',       cap: 'Up to €1,000,000',               statute: 'Real Decreto 193/2023, art. 35' },
  { country: 'Netherlands', cap: 'Up to €87,000',                  statute: 'Wet Toegankelijkheid, art. 8' },
  { country: 'Belgium',     cap: 'Up to €80,000',                  statute: 'Arrêté du 19 avril 2024' },
  { country: 'Poland',      cap: 'Up to PLN 50,000 (≈ €10,000)',   statute: 'Ustawa o dostępności, art. 32' },
  { country: 'Sweden',      cap: 'Up to SEK 500,000 (≈ €50,000)',  statute: 'Lag 2018:1937' },
];

export default function RoiCalculatorPage() {
  useSeo({
    title: 'European Accessibility Act penalties by member state',
    canonicalPath: '/roi-calculator',
    description:
      'A factual reference to how penalties work under national transpositions of the European Accessibility Act (Directive 2019/882), with published statutory caps by EU member state. Informational only — not legal advice.',
  });

  const user = useAuthStore(s => s.user);
  const scanCtaTarget = user ? '/' : '/sign-in';

  return (
    <div className="page roi-page">
      <header className="roi-header">
        <p className="roi-eyebrow">Reference</p>
        <h1>European Accessibility Act: penalties by member state</h1>
        <p className="roi-tagline">
          The European Accessibility Act (Directive 2019/882) is transposed into national law by
          each EU member state, and penalties for non-compliance are set by those national
          statutes. This page is a plain summary of the published caps, for general awareness.
        </p>
      </header>

      <section className="roi-disclaimer-band">
        <p>
          <strong>This page is informational and is not legal advice.</strong> It does not
          estimate the exposure of any specific business. Actual outcomes depend on the facts, the
          discretion of the national authority, and any remediation undertaken. For guidance on how
          the EAA applies to your situation, consult a lawyer qualified in EU accessibility or
          consumer law.
        </p>
      </section>

      <section className="roi-section">
        <h2>How penalties work under the EAA</h2>
        <ul className="roi-explainer">
          <li>
            The EAA is an EU directive: it sets common requirements, but each member state writes
            its own implementing law, designates its own enforcement authority, and sets its own
            penalties.
          </li>
          <li>
            The figures below are published first-offence maximums. Several member states apply a
            multiplier (typically 1–3×) for repeated or serious violations.
          </li>
          <li>
            Authorities generally seek remediation first; financial penalties are one tool among
            several and are applied at the regulator's discretion.
          </li>
          <li>
            EAA enforcement only began on 28 June 2025, so published case data is still limited and
            these figures may change as national practice develops.
          </li>
        </ul>
      </section>

      <section className="roi-section">
        <h2>Published statutory penalty caps</h2>
        <div className="eaa-penalty-table-wrap">
          <table className="eaa-penalty-table">
            <thead>
              <tr>
                <th scope="col">Member state</th>
                <th scope="col">Maximum penalty (published cap)</th>
                <th scope="col">National statute</th>
              </tr>
            </thead>
            <tbody>
              {PENALTIES.map((p) => (
                <tr key={p.country}>
                  <th scope="row">{p.country}</th>
                  <td>{p.cap}</td>
                  <td>{p.statute}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="muted small">
          Caps last reviewed 2026-05-26 and may not be current; currency conversions are
          approximate. Always verify against the official national source before relying on these
          figures.
        </p>
      </section>

      <section className="roi-cta-band">
        <h2>Where an audit fits in</h2>
        <p>
          A WCAG 2.1 AA audit is a practical first step in a compliance review: it identifies
          common, machine-detectable accessibility issues on your pages and produces a draft
          accessibility statement to build on. It is a starting point — not a substitute for a
          manual audit by a qualified accessibility professional or for legal advice.
        </p>
        <Link to={scanCtaTarget} className="cta">
          Run a WCAG audit — €29
        </Link>
        <p className="muted small">
          Prefer to look first? See a <Link to="/sample">sample report</Link> — no sign-up.
        </p>
      </section>
    </div>
  );
}
